// src/pages/UtahHistory.jsx
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaTrain, 
  FaFlag, 
  FaIndustry, 
  FaPlane, 
  FaMountain,
  FaUniversity,
  FaMedal,
  FaLeaf,
  FaRocket,
  FaUsers,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaPlay,
  FaPause,
  FaNewspaper
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import LandingSection from "../components/LandingSection";
import ActionButton from "../utils/ActionButton";
import { Link } from "react-router-dom";
import { historyMilestones, cn } from "./historyData";

const FloatingElements = ({ theme }) => {
  const isDark = theme === "modern-tech" || theme === "ultra-modern";

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
      {/* Vintage Sketches for early years */}
      {(theme === "vintage" || theme === "classic") && (
        <>
          <motion.div
            animate={{ 
              y: [0, -20, 0],
              rotate: [0, 5, 0]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-[10%] opacity-10"
          >
            <FaNewspaper size={120} />
          </motion.div>
          <motion.div
            animate={{ 
              y: [0, 20, 0],
              rotate: [0, -5, 0]
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-40 right-[15%] opacity-10"
          >
            <FaFlag size={150} />
          </motion.div>
        </>
      )}

      {/* Tech Particles for later years */}
      {(theme === "modern-tech" || theme === "ultra-modern") && (
        <>
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute bg-blue-500/20 rounded-full blur-xl"
              style={{
                width: Math.random() * 200 + 100,
                height: Math.random() * 200 + 100,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.3, 0.1],
                x: [0, Math.random() * 50 - 25, 0],
                y: [0, Math.random() * 50 - 25, 0],
              }}
              transition={{
                duration: Math.random() * 5 + 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          ))}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
            className="absolute -top-20 -right-20 text-blue-500/10"
          >
            <FaRocket size={400} />
          </motion.div>
        </>
      )}
    </div>
  );
};

const isDarkTheme = (theme) => {
  return ["space-age", "modern-tech", "ultra-modern"].includes(theme);
};

export default function UtahHistory() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [direction, setDirection] = useState(0);

  const nextMilestone = useCallback(() => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % historyMilestones.length);
  }, []);

  const prevMilestone = useCallback(() => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + historyMilestones.length) % historyMilestones.length);
  }, []);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(nextMilestone, 10000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, nextMilestone]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextMilestone();
      if (e.key === "ArrowLeft") prevMilestone();
      if (e.key === " ") {
        e.preventDefault();
        setIsPlaying(!isPlaying);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextMilestone, prevMilestone, isPlaying]);

  const current = historyMilestones[activeIndex];
  const isCurrentDark = isDarkTheme(current.theme);

  // Animation variants
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      rotateY: direction > 0 ? 45 : -45,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      rotateY: 0
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      rotateY: direction < 0 ? 45 : -45,
    })
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <FloatingElements theme={current.theme} />

      <Header />

      <LandingSection
        data={{
          imageName: "UTDT-History.webp",
          title: "Utah History",
          visible: "",
          overlay: "linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5))",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          heroHeight: "100vh",
        }}
      />

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 py-12">

        {/* Intro Section - Similar to NonProfits/Gigawatt */}
        <div className="mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-600 text-sm font-semibold mb-4"
          >
            A Journey Through Time
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-blue-950">
            The Beehive State&apos;s Story
          </h1>
          <p className="max-w-3xl mx-auto text-lg leading-relaxed text-slate-800">
            From the arrival of the railroad in 1890 to Utah&apos;s deep-tech momentum today, 
            explore the milestones that shaped Utah&apos;s unique identity and booming economy.
          </p>
        </div>

        {/* Interactive Carousel */}
        <div className="relative mb-24 min-h-[500px] flex items-center justify-center">



          {/* Milestone Display */}
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={activeIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.4 }
              }}
              className="w-full"
            >
              <div className={cn(
                "rounded-3xl p-8 md:p-16 shadow-2xl relative overflow-hidden transition-all duration-1000",
                current.theme === "vintage" ? "bg-amber-50 border-4 border-amber-200 font-serif" :
                current.theme === "classic" ? "bg-emerald-50 border-4 border-emerald-200 font-serif" :
                current.theme === "mid-century" ? "bg-blue-50 border-t-8 border-blue-600 shadow-blue-100" :
                current.theme === "modernist" ? "bg-cyan-50 border-t-8 border-cyan-500 shadow-cyan-100" :
                current.theme === "modern" ? "bg-white border border-rose-100" :
                "bg-slate-800 border border-blue-500/30 text-white backdrop-blur-xl"
              )}>
                {/* Theme-specific background patterns */}
                {current.theme === "vintage" && (
                  <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#8B4513 0.5px, transparent 0.5px)', backgroundSize: '20px 20px' }} />
                )}
                {current.theme === "modern-tech" && (
                  <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'linear-gradient(45deg, transparent 48%, #3B82F6 50%, transparent 52%)', backgroundSize: '60px 60px' }} />
                )}

                <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
                  <div className="flex flex-col h-full justify-between">
                    <div>
                      {/* Icon Card for Slider */}
                      <div className={cn(
                        "hidden md:flex w-20 h-20 mb-8 items-center justify-center rounded-2xl text-4xl shadow-inner relative group/slider-icon",
                        isCurrentDark ? "bg-white/10 text-emerald-400" : "bg-blue-600/5 text-blue-600"
                      )}>
                        <div className={cn(
                          "absolute inset-0 rounded-2xl opacity-20 blur-xl transition-all duration-500 group-hover/slider-icon:opacity-40",
                          isCurrentDark ? "bg-emerald-400" : "bg-blue-600"
                        )} />
                        <div className="relative z-10 transition-transform duration-500 group-hover/slider-icon:scale-110 group-hover/slider-icon:rotate-3">
                          {current.icon}
                        </div>
                      </div>

                      <span className={cn(
                        "text-sm font-bold tracking-widest uppercase mb-4 block",
                        isCurrentDark ? "text-blue-300" : "text-blue-800"
                      )}>
                        {current.category} • {current.year}
                      </span>
                      <h2 className={cn(
                        "text-4xl md:text-5xl font-bold mb-6",
                        current.theme === "vintage" || current.theme === "classic" ? "font-serif text-amber-950" : ""
                      )}>
                        {current.title}
                      </h2>
                    <p className={cn(
                      "text-lg mb-8 leading-relaxed",
                      isCurrentDark ? "text-slate-100" : "text-slate-900"
                    )}>
                      {current.description}
                    </p>
                      <div className="pt-2">
                        <ActionButton 
                          data="Discover Full Story" 
                          link={`/utah-at-a-glance/utah-history/${current.year}`}
                          internal={true}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <motion.div 
                      key={current.image}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className={cn(
                        "w-64 h-64 md:w-96 md:h-80 rounded-2xl overflow-hidden shadow-2xl transition-all duration-1000 relative group border-4",
                        current.theme === "vintage" ? "border-amber-300" :
                        current.theme === "modern-tech" ? "border-blue-400/50 shadow-[0_0_50px_rgba(59,130,246,0.3)]" :
                        current.theme === "ultra-modern" ? "border-violet-400/50 shadow-[0_0_70px_rgba(139,92,246,0.4)]" :
                        "border-white"
                      )}
                    >
                      <img 
                        src={current.image} 
                        alt={current.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      {current.theme === "ultra-modern" && (
                        <motion.div 
                          className="absolute inset-0 border-4 border-violet-400/30 pointer-events-none"
                          animate={{ opacity: [0.2, 0.5, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      )}
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>          {/* Side Navigation Arrows */}
          <div className="absolute top-1/2 -left-4 md:-left-12 z-30 -translate-y-1/2">
            <button 
              onClick={prevMilestone}
              className="group w-12 h-12 flex items-center justify-center rounded-full transition-all duration-300 border backdrop-blur-xl shadow-xl hover:scale-110 bg-white border-slate-200 text-slate-800 hover:border-blue-500 shadow-slate-200"
              aria-label="Previous Milestone"
            >
              <FaChevronLeft className="text-xl group-hover:-translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="absolute top-1/2 -right-4 md:-right-12 z-30 -translate-y-1/2">
            <button 
              onClick={nextMilestone}
              className="group w-12 h-12 flex items-center justify-center rounded-full transition-all duration-300 border backdrop-blur-xl shadow-xl hover:scale-110 bg-white border-slate-200 text-slate-800 hover:border-blue-500 shadow-slate-200"
              aria-label="Next Milestone"
            >
              <FaChevronRight className="text-xl group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Significant Moments - Vertical Timeline */}
        <section className="py-24 max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-blue-900">
              Significant Moments & Growth
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full" />
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-[31px] md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700 md:-translate-x-1/2" />

            {historyMilestones.map((item, idx) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className={cn(
                  "relative mb-16 flex flex-col md:flex-row items-center w-full",
                  idx % 2 === 0 ? "md:flex-row-reverse" : ""
                )}
              >
                {/* Timeline Dot */}
                <div className="absolute left-[31px] md:left-1/2 -translate-x-1/2 w-6 h-6 rounded-full border-4 border-white bg-blue-600 z-10 shadow-lg" />

                {/* Content Box */}
                <div className={cn(
                  "w-full md:w-[45%] pl-20 md:pl-0 flex",
                  idx % 2 === 0 ? "md:pr-12 md:text-right md:justify-end" : "md:pl-12 md:text-left"
                )}>
                  <motion.div 
                    onClick={() => {
                      setDirection(idx > activeIndex ? 1 : -1);
                      setActiveIndex(idx);
                      window.scrollTo({ top: 300, behavior: 'smooth' });
                    }}
                    className={cn(
                      "p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group shadow-lg border w-full relative overflow-hidden backdrop-blur-sm",
                      activeIndex === idx ? "ring-2 ring-blue-500 shadow-blue-200" : "",
                      `bg-gradient-to-br ${item.colors.from} ${item.colors.to} border-gray-100 shadow-slate-200 hover:shadow-slate-300`
                    )}
                    whileHover={{ y: -8 }}
                  >
                    {/* Background Gradient on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Icon Card */}
                    <div className={cn(
                      "w-20 h-20 mb-6 flex items-center justify-center rounded-xl text-3xl shadow-inner relative group/icon",
                      `bg-white/20 border border-white/30`
                    )}>
                      <div className={cn(
                        "absolute inset-0 rounded-xl opacity-0 blur-lg transition-all duration-500 group-hover:opacity-20",
                        `bg-${item.colors.accent}`
                      )} />
                      <div className={cn(
                        "relative z-10 transition-transform duration-500 group-hover:scale-110",
                        `text-${item.colors.accent}`
                      )}>
                        {item.icon}
                      </div>
                    </div>
                    <div className={cn(
                      "text-sm font-black mb-2",
                      `text-${item.colors.accent}`
                    )}>
                      {item.year}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-gray-700">
                      {item.description}
                    </p>
                  </motion.div>
                </div>

                {/* Date Label (Desktop Only) */}
                <div className={cn(
                  "hidden md:block w-[45%] font-black text-6xl transition-all duration-700",
                  `text-${item.colors.accent} opacity-60`,
                  idx % 2 === 0 ? "text-left pl-12" : "text-right pr-12"
                )}>
                  {item.year}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Call to Action - ActionButton from utils */}
        <section className="py-16 text-center border-t border-gray-200 dark:border-gray-800">
          <p className={cn(
            "text-lg mb-8 italic",
            isCurrentDark ? "text-slate-400" : "text-gray-600"
          )}>
            Explore more data and insights on Utah&apos;s journey
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <ActionButton data="Fun Facts" link="/utah-at-a-glance/utah-funfacts" internal={true} />
            <ActionButton data="Economic Verticals" link="/verticals" internal={true} />
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
// import React, { useState, useEffect, useCallback } from "react";
// import CinematicHero from "../components/CinematicHero";
// import StorySection from "../components/StorySection";
// import ParallaxTimeline from "../components/ParallaxTimeline";
// import UtahMapMilestones from "../components/UtahMapMilestones";
// import TimelineProgress from "../components/TimelineProgress";
// import { historyMilestones } from "./historyData";

// export default function UtahHistory() {

//   return (

//     <div className="bg-white">

//       <TimelineProgress />

//       <CinematicHero />

//       <ParallaxTimeline milestones={historyMilestones} />

//       <UtahMapMilestones />

//       {historyMilestones.map((milestone) => (

//         <StorySection
//           key={milestone.year}
//           milestone={milestone}
//         />

//       ))}

//     </div>

//   )

// }
